import type { Href } from "expo-router";

/** The Backfill page's segment, which the landing page offers only behind its flag. */
export const BACKFILL_SUBPATH = "backfill";

/** The Recompute incidents page's segment, which the landing page offers only behind its flag. */
export const RECOMPUTE_INCIDENTS_SUBPATH = "recompute-incidents";

/** The subtree every Patriot tools page lives under, below the tenant. */
export const PATRIOT_TOOLS_SEGMENT = "patriot-tools";

export function patriotToolsHref(tenantId: string, subpath = "", query: Record<string, string | undefined> = {}): Href {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, value);
  }
  const search = params.toString();
  const path = `/tenants/${tenantId}/${PATRIOT_TOOLS_SEGMENT}${subpath ? `/${subpath}` : ""}`;
  // Typed routes cannot see a path assembled at runtime.
  return (search ? `${path}?${search}` : path) as Href;
}

/**
 * Where an activation opens: its tenant's own page when the directory holds the
 * tenant, otherwise the fleet page, which serves a ticket whichever tenant — or
 * none — it belongs to.
 */
export function activationHref(tenantId: string, ticketNo: string, knownOwner: string | undefined): Href {
  const ticket = encodeURIComponent(ticketNo);
  return knownOwner === undefined ?
      patriotToolsHref(tenantId, `fleet-activations/${ticket}`)
    : patriotToolsHref(knownOwner, `tenant-activations/${ticket}`);
}

export const FLEET_CLIENTS_SUBPATH = "fleet-clients";
export const FLEET_ACTIVATIONS_SUBPATH = "fleet-activations";
export const TENANT_INCIDENTS_SUBPATH = "tenant-incidents";
export const FLEET_INCIDENTS_SUBPATH = "fleet-incidents";

/**
 * Where an incident opens: its tenant's own page when the directory holds the
 * tenant, otherwise the fleet page, as for an activation.
 */
export function incidentHref(tenantId: string, incidentId: string, knownOwner: string | undefined): Href {
  const id = encodeURIComponent(incidentId);
  return knownOwner === undefined ?
      patriotToolsHref(tenantId, `${FLEET_INCIDENTS_SUBPATH}/${id}`)
    : patriotToolsHref(knownOwner, `${TENANT_INCIDENTS_SUBPATH}/${id}`);
}

/** The wall clock `timeZone` shows at `at`, to the minute: `YYYY-MM-DDTHH:mm`, as the list filters read it. */
function wallClockIn(at: Date, timeZone: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(at)
      .map((part) => [part.type, part.value]),
  );
  return `${parts["year"] ?? ""}-${parts["month"] ?? ""}-${parts["day"] ?? ""}T${parts["hour"] ?? ""}:${parts["minute"] ?? ""}`;
}

/**
 * One client's incidents on the fleet page, over the default window — ending on
 * the day `endingAt.at` falls on in its `timeZone` when given, so the incident
 * it was opened from is listed; otherwise ending today in the viewer's zone.
 */
export function clientIncidentsHref(
  tenantId: string,
  clientNo: string,
  endingAt?: { readonly at: string; readonly timeZone: string },
): Href {
  return patriotToolsHref(tenantId, FLEET_INCIDENTS_SUBPATH, {
    clientNo,
    ...(endingAt !== undefined && {
      to: wallClockIn(new Date(endingAt.at), endingAt.timeZone).slice(0, 10),
      tz: endingAt.timeZone,
    }),
  });
}

export const FLEET_OPERATOR_NOTES_SUBPATH = "fleet-operator-notes";

/**
 * A client's operator notes in the minutes around `at`: where a call made
 * outside any activation can be read, since no activation page holds its note.
 */
export function operatorNotesAroundHref(tenantId: string, clientNo: string, at: string, timeZone: string): Href {
  const instant = Date.parse(at);
  return patriotToolsHref(tenantId, FLEET_OPERATOR_NOTES_SUBPATH, {
    clientNo,
    from: wallClockIn(new Date(instant - 60_000), timeZone),
    to: wallClockIn(new Date(instant + 60_000), timeZone),
    tz: timeZone,
  });
}

export function fleetClientHref(tenantId: string, clientNo: string): Href {
  return patriotToolsHref(tenantId, `${FLEET_CLIENTS_SUBPATH}/${encodeURIComponent(clientNo)}`);
}

// Every fleet list matches `?clientNo=` as a prefix; a whole client number is
// a prefix of no other, so it finds just that client.

export function clientActivationsHref(tenantId: string, clientNo: string): Href {
  return patriotToolsHref(tenantId, FLEET_ACTIVATIONS_SUBPATH, { clientNo });
}

export function clientOperatorNotesHref(tenantId: string, clientNo: string): Href {
  return patriotToolsHref(tenantId, FLEET_OPERATOR_NOTES_SUBPATH, { clientNo });
}

export interface PatriotTool {
  readonly subpath: string;
  readonly title: string;
  readonly description: string;
}

export interface PatriotToolSection {
  readonly title: string;
  readonly tools: readonly PatriotTool[];
}

/** The pages listed on the Patriot tools landing page, by section. Add a page here to list it. */
export const PATRIOT_TOOL_SECTIONS: readonly PatriotToolSection[] = [
  {
    title: "Organisation queries",
    tools: [
      {
        subpath: TENANT_INCIDENTS_SUBPATH,
        title: "Organisation incidents",
        description: "Genuine incidents and activation totals for this organisation",
      },
      {
        subpath: "tenant-activations",
        title: "Organisation activations",
        description: "Patriot activations for this organisation",
      },
      {
        subpath: "tenant-signals",
        title: "Organisation signals",
        description: "Patriot signals for this organisation",
      },
    ],
  },
  {
    title: "Fleet queries",
    tools: [
      {
        subpath: FLEET_INCIDENTS_SUBPATH,
        title: "Fleet incidents",
        description: "Genuine incidents and activation totals across the whole fleet",
      },
      {
        subpath: FLEET_ACTIVATIONS_SUBPATH,
        title: "Fleet activations",
        description: "Patriot activations across the whole fleet",
      },
      {
        subpath: FLEET_CLIENTS_SUBPATH,
        title: "Fleet clients",
        description: "Patriot clients across the whole fleet",
      },
      {
        subpath: FLEET_OPERATOR_NOTES_SUBPATH,
        title: "Fleet operator notes",
        description: "Patriot operator notes across the whole fleet, or for one asset's clients",
      },
    ],
  },
  {
    title: "Commands",
    tools: [
      {
        subpath: "connection-check",
        title: "Connection check",
        description: "Check that a3s can reach Patriot and read its data",
      },
      {
        subpath: BACKFILL_SUBPATH,
        title: "Backfill",
        description: "Copy Patriot records into a3s over a range of time",
      },
      {
        subpath: RECOMPUTE_INCIDENTS_SUBPATH,
        title: "Recompute incidents",
        description: "Count genuine incidents again over whole days",
      },
    ],
  },
];
